import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/components/dash";

export default function NotificationBell({ onOpenLink }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const box = useRef(null);

  const load = useCallback(() => {
    api.get("/notifications?limit=20").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      await api.post("/notifications/read-all").catch(() => {});
      setTimeout(load, 1500);
    }
  };

  const go = (n) => {
    setOpen(false);
    if (!n.link) return;
    const [path, query] = n.link.split("?");
    const tab = new URLSearchParams(query || "").get("tab");
    if (tab && onOpenLink) onOpenLink(tab);
    else navigate(path);
  };

  return (
    <div className="relative" ref={box}>
      <button onClick={toggle} className="relative h-10 w-10 rounded-full border border-border bg-white flex items-center justify-center hover:bg-slate-50" aria-label="Notifications" data-testid="notif-bell">
        <Bell className="h-[18px] w-[18px]" />
        {data.unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-count">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[min(92vw,360px)] bg-white rounded-xl border border-border shadow-xl z-30 overflow-hidden" data-testid="notif-panel">
          <div className="px-4 py-3 border-b border-border font-semibold text-sm">Notifications</div>
          <div className="max-h-[380px] overflow-y-auto divide-y divide-border">
            {data.items.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">You're all caught up.</p>}
            {data.items.map((n) => (
              <button key={n.id} onClick={() => go(n)} className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${n.read ? "" : "bg-primary/5"}`}>
                <p className="text-sm font-medium">{n.title}</p>
                {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                <p className="text-[11px] text-muted-foreground/80 mt-1">{fmtDateTime(n.created_at)}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
